import { ProductPrice } from './types/product.type';

export interface BillingProductSchema {
  core_galleries: number;
  core_storage: number;
  tenant_seats: number;
  scopes: string[];
}

export interface BillingStorageAddonProductSchema {
  core_storage_addon: number;
}

export type BillingPrices = Record<string, BillingProductSchema | BillingStorageAddonProductSchema | object>;

export const concatUniqueArray = <T>(...args: T[][]): T[] => {
  return [...new Set(args.flat())];
};

const getBytesFromKilobytes = (kilobytes: bigint): bigint => {
  return kilobytes * 1024n;
};

const getBytesFromMegabytes = (megabytes: bigint): bigint => {
  return getBytesFromKilobytes(megabytes) * 1024n;
};

const getBytesFromGigabytes = (gigabytes: bigint): bigint => {
  return getBytesFromMegabytes(gigabytes) * 1024n;
};

type BillingScopes = typeof billingScopes;
type CoreScopes = BillingScopes['core'][keyof BillingScopes['core']];
type AssetScopes = BillingScopes['asset'][keyof BillingScopes['asset']];
type AssetVersionScopes = BillingScopes['assetVersion'][keyof BillingScopes['assetVersion']];
type GalleryScopes = BillingScopes['gallery'][keyof BillingScopes['gallery']];
type GalleryPresetsScopes = BillingScopes['galleryPresets'][keyof BillingScopes['galleryPresets']];
type TenantScopes = BillingScopes['tenant'][keyof BillingScopes['tenant']];
type WatermarkScopes = BillingScopes['watermark'][keyof BillingScopes['watermark']];
type GroupsScopes = BillingScopes['groups'][keyof BillingScopes['groups']];
type GroupUsersScopes = BillingScopes['groupUsers'][keyof BillingScopes['groupUsers']];
type GroupGalleriesScopes = BillingScopes['groupGalleries'][keyof BillingScopes['groupGalleries']];

export type BillingScope =
  | CoreScopes
  | AssetScopes
  | AssetVersionScopes
  | GalleryScopes
  | GalleryPresetsScopes
  | TenantScopes
  | WatermarkScopes
  | GroupsScopes
  | GroupUsersScopes
  | GroupGalleriesScopes;

export const billingScopes = {
  core: {
    storageAddons: 'core:storage-addons',
  },
  asset: {
    favourites: 'asset:favourites',
    comments: 'asset:comments',
    flags: 'asset:flags',
    status: 'asset:status',
    colorLabels: 'asset:color-labels',
    customLabels: 'asset:custom-labels',
    upload: 'asset:upload',
    uploadRaw: 'asset:upload-raw',
    download: 'asset:download',
    downloadOriginal: 'asset:download-original',
    watermarking: 'asset:watermarking',
    fileAttachment: 'asset:file-attachment',
    embedRightClickProtection: 'asset:embed-right-click-protection',
    video: 'asset:video',
    createCdnLink: 'asset:create-cdn-link',
    customVideoPoster: 'asset:custom-video-poster',
  },
  assetVersion: {
    multiple: 'asset-version:multiple',
  },
  gallery: {
    password: 'gallery:password',
    subGallery: 'gallery:sub-gallery',
    embedSection: 'gallery:embed-section',
    embedGallery: 'gallery:embed-gallery',
    clientAdvancedMode: 'gallery:client-advanced-mode',
    clientLayoutMode: 'gallery:client-layout-mode',
    approvalLimit: 'gallery:approval-limit',
    removePicflowBranding: 'gallery:remove-picflow-branding',
    customPresets: 'gallery:custom-presets',
    forceIdentification: 'gallery:force-identification',
    approvedDownloadsOnly: 'gallery:approved-downloads-only',
    downloadFromEmbed: 'gallery:download-from-embed',
    videoInCover: 'gallery:video-in-cover',
  },
  galleryPresets: {
    approvedDownloadsOnly: 'gallery-presets:approved-downloads-only',
  },
  tenant: {
    logoUpload: 'tenant:logo-upload',
    customDomain: 'tenant:custom-domain',
    customFonts: 'tenant:custom-fonts',
    customGoogleAnalytics: 'tenant:custom-google-analytics',
    blockPublicLinkAccess: 'tenant:block-public-link-access',
    customGoogleTagManager: 'tenant:custom-google-tag-manager',
    enforce2fa: 'tenant:enforce-2fa',
  },
  watermark: {
    update: 'watermark:update',
    create: 'watermark:create',
  },
  groups: {
    list: 'groups:list',
    create: 'groups:create',
    retrieve: 'groups:retrieve',
    update: 'groups:update',
    delete: 'groups:delete',
  },
  groupUsers: {
    add: 'group-users:add',
    remove: 'group-users:remove',
    list: 'group-users:list',
  },
  groupGalleries: {
    list: 'group-galleries:list',
    attach: 'group-galleries:attach',
    detach: 'group-galleries:detach',
    retrieve: 'group-galleries:retrieve',
    update: 'group-galleries:update',
  },
} as const;

export interface ProductConfig {
  core_galleries: number;
  included_seat_count?: number;
  core_storage: bigint;
  scopes: BillingScope[];
}

const freeProduct: ProductConfig = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(2n),
  included_seat_count: 1,
  scopes: [
    billingScopes.asset.favourites,
    billingScopes.asset.comments,
    billingScopes.asset.upload,
    billingScopes.asset.download,
    billingScopes.asset.downloadOriginal,
    billingScopes.asset.flags,
    billingScopes.asset.status,
    billingScopes.gallery.clientAdvancedMode,
    billingScopes.tenant.logoUpload,
  ],
};
const basicProduct: ProductConfig = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(10n),
  included_seat_count: 1,
  scopes: concatUniqueArray(freeProduct.scopes, [
    billingScopes.core.storageAddons,
    billingScopes.asset.colorLabels,
    billingScopes.assetVersion.multiple,
    billingScopes.gallery.subGallery,
    billingScopes.gallery.embedSection,
    billingScopes.gallery.removePicflowBranding,
    billingScopes.gallery.password,
    billingScopes.gallery.forceIdentification,
  ]),
};
const plusProduct: ProductConfig = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(100n),
  included_seat_count: 1,
  scopes: concatUniqueArray(basicProduct.scopes, [
    billingScopes.gallery.embedGallery,
    billingScopes.gallery.downloadFromEmbed,
    billingScopes.asset.embedRightClickProtection,
    billingScopes.asset.video,
  ]),
};
const proProduct: ProductConfig = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(1000n),
  included_seat_count: 1,
  scopes: concatUniqueArray(plusProduct.scopes, [
    billingScopes.asset.customLabels,
    billingScopes.asset.uploadRaw,
    billingScopes.asset.watermarking,
    billingScopes.asset.fileAttachment,
    billingScopes.asset.createCdnLink,
    billingScopes.asset.customVideoPoster,
    billingScopes.gallery.clientLayoutMode,
    billingScopes.gallery.approvalLimit,
    billingScopes.gallery.customPresets,
    billingScopes.gallery.approvedDownloadsOnly,
    billingScopes.gallery.videoInCover,
    billingScopes.galleryPresets.approvedDownloadsOnly,
    billingScopes.tenant.customDomain,
    billingScopes.tenant.customFonts,
    billingScopes.tenant.customGoogleAnalytics,
    billingScopes.watermark.create,
    billingScopes.watermark.update,
  ]),
};
const teamProduct: ProductConfig = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(2000n),
  included_seat_count: 1,
  scopes: concatUniqueArray(proProduct.scopes, [billingScopes.tenant.customGoogleTagManager]),
};
const enterpriseProduct: ProductConfig = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(10000n),
  included_seat_count: 1,
  scopes: concatUniqueArray(teamProduct.scopes, [
    billingScopes.groups.list,
    billingScopes.groups.create,
    billingScopes.groups.retrieve,
    billingScopes.groups.update,
    billingScopes.groups.delete,
    billingScopes.groupUsers.add,
    billingScopes.groupUsers.remove,
    billingScopes.groupUsers.list,
    billingScopes.groupGalleries.attach,
    billingScopes.groupGalleries.detach,
    billingScopes.groupGalleries.list,
    billingScopes.groupGalleries.update,
    billingScopes.tenant.blockPublicLinkAccess,
    billingScopes.tenant.enforce2fa,
  ]),
};

export const productConfig: { [key: string]: ProductConfig } = {
  'Picflow Free': freeProduct,
  'Picflow Basic': basicProduct,
  'Picflow Plus': plusProduct,
  'Picflow Pro': proProduct,
  'Picflow Team': teamProduct,
  'Picflow Enterprise': enterpriseProduct,
};

export const freeProductPrice = <ProductPrice>{ amount: 0 };

export const freePlanMaxSeats = 2;
